import React, { useState, useEffect } from "react";
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where } from "firebase/firestore";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { db, auth } from "../firebase";
import DashboardBase from "../DashboardBase";

const EmpresasManager = () => {
  const [empresas, setEmpresas] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDepartamentosForm, setShowDepartamentosForm] = useState(false);
  const [showCargosForm, setShowCargosForm] = useState(false);
  const [editingEmpresa, setEditingEmpresa] = useState(null);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  
  // Estados de autenticación
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authEmail, setAuthEmail] = useState('admin@ossinnovation.com');
  const [authPassword, setAuthPassword] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  
  const [formData, setFormData] = useState({
    nombre: "",
    nitCedula: "",
    direccion: "",
    sede: "",
    pais: "",
    telefono: "",
    email: "",
    contacto: "",
    estado: "activo"
  });
  
  const [departamentoData, setDepartamentoData] = useState({
    nombre: "",
    nivel: "corporativo", // corporativo, divisional, departamental, seccional, operativo
    parentId: "",
    empresaId: ""
  });
  
  const [cargoData, setCargoData] = useState({
    nombre: "",
    departamentoId: "",
    empresaId: ""
  });

  const nivelesJerarquicos = [
    { value: "corporativo", label: "Corporativo", description: "Nivel más alto de la organización" },
    { value: "divisional", label: "Divisional", description: "Grandes divisiones o unidades de negocio" },
    { value: "departamental", label: "Departamental", description: "Departamentos principales" },
    { value: "seccional", label: "Seccional", description: "Secciones dentro de departamentos" },
    { value: "operativo", label: "Operativo", description: "Nivel operativo y de ejecución" }
  ];

  // Cargar empresas desde Firestore
  const loadEmpresas = async () => {
    console.log("📥 Iniciando carga de empresas...");
    try {
      console.log("🗄️ Conectando a Firestore...");
      const querySnapshot = await getDocs(collection(db, "empresas"));
      console.log("📊 Documentos encontrados:", querySnapshot.size);
      
      const empresasData = querySnapshot.docs.map(doc => {
        const data = { id: doc.id, ...doc.data() };
        console.log("📄 Empresa cargada:", data);
        return data;
      });
      
      console.log("📋 Total empresas cargadas:", empresasData.length);
      setEmpresas(empresasData);
    } catch (error) {
      console.error("❌ Error al cargar empresas:", error);
      console.error("❌ Código de error:", error.code);
      console.error("❌ Mensaje de error:", error.message);
    } finally {
      setLoading(false);
      console.log("✅ Carga de empresas finalizada");
    }
  };

  // Cargar departamentos de una empresa
  const loadDepartamentos = async (empresaId) => {
    try {
      const q = query(collection(db, "departamentos"), where("empresaId", "==", empresaId));
      const querySnapshot = await getDocs(q);
      const departamentosData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDepartamentos(departamentosData);
    } catch (error) {
      console.error("Error al cargar departamentos:", error);
    }
  };

  // Cargar cargos de una empresa
  const loadCargos = async (empresaId) => {
    try {
      const q = query(collection(db, "posiciones"), where("empresaId", "==", empresaId));
      const querySnapshot = await getDocs(q);
      const cargosData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCargos(cargosData);
    } catch (error) {
      console.error("Error al cargar cargos:", error);
    }
  };

  // Función de autenticación con email/password
  const authenticateUser = async () => {
    try {
      console.log("🔐 Autenticando usuario con email/password...");
      if (!auth.currentUser) {
        console.log("📧 Intentando login con:", authEmail);
        const userCredential = await signInWithEmailAndPassword(auth, authEmail, authPassword);
        console.log("✅ Usuario autenticado:", userCredential.user.email);
        setCurrentUser(userCredential.user);
        setIsAuthenticated(true);
        return true;
      } else {
        console.log("✅ Usuario ya autenticado:", auth.currentUser.email);
        setIsAuthenticated(true);
        return true;
      }
    } catch (error) {
      console.error("❌ Error en autenticación:", error.message);
      setIsAuthenticated(false);
      return false;
    }
  };

  // Función para cerrar sesión
  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsAuthenticated(false);
      setCurrentUser(null);
      console.log("🚪 Sesión cerrada");
    } catch (error) {
      console.error("❌ Error al cerrar sesión:", error);
    }
  };

  // Función de login manual
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!authPassword) {
      alert("Por favor ingresa la contraseña");
      return;
    }
    const success = await authenticateUser();
    if (!success) {
      alert("Error de autenticación. Verifica email y contraseña.");
    }
  };

  // Función de prueba de conexión a Firebase
  const testFirebaseConnection = async () => {
    console.log("🧪 Probando conexión a Firebase...");
    try {
      // Verificar que el usuario esté autenticado
      if (!isAuthenticated || !currentUser) {
        console.log("ℹ️ Usuario no autenticado, saltando test de conexión");
        return false;
      }

      // Intentar una operación simple de lectura
      const testCollection = collection(db, "test");
      console.log("✅ Collection creada exitosamente");
      return true;
    } catch (error) {
      console.error("❌ Error en conexión a Firebase:", error);
      console.error("❌ Código:", error.code);
      console.error("❌ Mensaje:", error.message);
      return false;
    }
  };

  useEffect(() => {
    console.log("🚀 Componente EmpresasManager iniciando...");
    console.log("🔧 Verificando configuración de Firebase:");
    console.log("- DB Object:", db);
    console.log("- Auth Object:", auth);
    
    if (db && db.app) {
      console.log("- DB App:", db.app);
      console.log("- App Name:", db.app.name);
    } else {
      console.log("❌ DB o DB.app no están definidos");
    }
    
    // Verificar si hay un usuario autenticado al cargar el componente
    if (auth.currentUser) {
      console.log("✅ Usuario ya autenticado:", auth.currentUser.email);
      setIsAuthenticated(true);
      setCurrentUser(auth.currentUser);
      loadEmpresas();
    } else {
      console.log("ℹ️ No hay usuario autenticado, mostrando login");
      setLoading(false);
    }
  }, []);

  // useEffect separado para cargar datos cuando se autentica
  useEffect(() => {
    if (isAuthenticated && currentUser) {
      testFirebaseConnection().then((connected) => {
        if (connected) {
          console.log("✅ Firebase conectado, cargando empresas...");
          loadEmpresas();
        } else {
          console.log("❌ No se pudo conectar a Firebase");
          setLoading(false);
        }
      });
    }
  }, [isAuthenticated, currentUser]);

  // Guardar empresa (crear o actualizar)
  const handleSave = async (e) => {
    e.preventDefault();
    console.log("🔥 Iniciando proceso de guardado...");
    console.log("📋 Datos del formulario:", formData);
    
    // Verificar que todos los campos requeridos estén completos
    if (!formData.nombre || !formData.nitCedula || !formData.sede || !formData.pais) {
      alert("Por favor completa todos los campos requeridos.");
      return;
    }
    
    try {
      console.log("� Verificando autenticación...");
      const authenticated = await authenticateUser();
      if (!authenticated) {
        throw new Error("No se pudo autenticar el usuario");
      }
      
      console.log("�🗄️ Verificando conexión DB...");
      if (!db) {
        throw new Error("Base de datos no inicializada");
      }
      
      let empresaId;
      if (editingEmpresa) {
        console.log("✏️ Actualizando empresa existente:", editingEmpresa.id);
        // Actualizar empresa existente
        const empresaRef = doc(db, "empresas", editingEmpresa.id);
        await updateDoc(empresaRef, {
          ...formData,
          fechaModificacion: new Date()
        });
        empresaId = editingEmpresa.id;
        console.log("✅ Empresa actualizada exitosamente:", empresaId);
      } else {
        console.log("➕ Creando nueva empresa...");
        const empresasCollection = collection(db, "empresas");
        console.log("📝 Datos a guardar:", {
          ...formData,
          fechaCreacion: new Date(),
          fechaModificacion: new Date()
        });
        
        // Crear nueva empresa
        const docRef = await addDoc(empresasCollection, {
          ...formData,
          fechaCreacion: new Date(),
          fechaModificacion: new Date()
        });
        empresaId = docRef.id;
        console.log("✅ Nueva empresa creada con ID:", empresaId);
      }
      
      console.log("🔄 Actualizando estados del componente...");
      setShowForm(false);
      setEditingEmpresa(null);
      setSelectedEmpresa(null);
      setFormData({
        nombre: "",
        nitCedula: "",
        direccion: "",
        sede: "",
        pais: "",
        telefono: "",
        email: "",
        contacto: "",
        estado: "activo"
      });
      
      console.log("📥 Recargando lista de empresas...");
      await loadEmpresas();
      
      // Mostrar mensaje de éxito
      alert(editingEmpresa ? "Empresa actualizada exitosamente." : "Empresa creada exitosamente.");
      console.log("🎉 Proceso completado exitosamente");
    } catch (error) {
      console.error("❌ Error detallado al guardar empresa:", error);
      console.error("❌ Código de error:", error.code);
      console.error("❌ Mensaje de error:", error.message);
      if (error.stack) {
        console.error("❌ Stack trace:", error.stack);
      }
      alert("Error al guardar la empresa: " + error.message);
    }
  };

  // Guardar departamento
  const handleSaveDepartamento = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, "departamentos"), {
        ...departamentoData,
        empresaId: selectedEmpresa.id,
        fechaCreacion: new Date()
      });
      
      setDepartamentoData({
        nombre: "",
        nivel: "corporativo",
        parentId: "",
        empresaId: ""
      });
      
      loadDepartamentos(selectedEmpresa.id);
      
      // Mostrar botón para crear cargos
      setShowCargosForm(true);
      alert("Departamento creado exitosamente. Ahora puedes crear cargos.");
    } catch (error) {
      console.error("Error al guardar departamento:", error);
      alert("Error al guardar el departamento.");
    }
  };

  // Guardar cargo
  const handleSaveCargo = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, "posiciones"), {
        ...cargoData,
        empresaId: selectedEmpresa.id,
        fechaCreacion: new Date()
      });
      
      setCargoData({
        nombre: "",
        departamentoId: "",
        empresaId: ""
      });
      
      loadCargos(selectedEmpresa.id);
      alert("Cargo creado exitosamente.");
    } catch (error) {
      console.error("Error al guardar cargo:", error);
      alert("Error al guardar el cargo.");
    }
  };

  // Editar empresa
  const handleEdit = (empresa) => {
    setEditingEmpresa(empresa);
    setFormData({
      nombre: empresa.nombre,
      nitCedula: empresa.nitCedula || empresa.ruc || "",
      direccion: empresa.direccion,
      sede: empresa.sede || "",
      pais: empresa.pais || "",
      telefono: empresa.telefono,
      email: empresa.email,
      contacto: empresa.contacto,
      estado: empresa.estado
    });
    setShowForm(true);
  };

  // Manejar departamentos de empresa
  const handleManageDepartamentos = (empresa) => {
    setSelectedEmpresa(empresa);
    loadDepartamentos(empresa.id);
    setShowDepartamentosForm(true);
  };

  // Manejar cargos de empresa
  const handleManageCargos = (empresa) => {
    setSelectedEmpresa(empresa);
    loadDepartamentos(empresa.id);
    loadCargos(empresa.id);
    setShowCargosForm(true);
  };

  // Eliminar empresa
  const handleDelete = async (empresaId) => {
    if (window.confirm("¿Estás seguro de que deseas eliminar esta empresa?")) {
      try {
        await deleteDoc(doc(db, "empresas", empresaId));
        loadEmpresas();
      } catch (error) {
        console.error("Error al eliminar empresa:", error);
        alert("Error al eliminar la empresa.");
      }
    }
  };

  // Cancelar formulario
  const handleCancel = () => {
    setShowForm(false);
    setShowDepartamentosForm(false);
    setShowCargosForm(false);
    setEditingEmpresa(null);
    setSelectedEmpresa(null);
    setFormData({
      nombre: "",
      nitCedula: "",
      direccion: "",
      sede: "",
      pais: "",
      telefono: "",
      email: "",
      contacto: "",
      estado: "activo"
    });
    setDepartamentoData({
      nombre: "",
      nivel: "corporativo",
      parentId: "",
      empresaId: ""
    });
    setCargoData({
      nombre: "",
      departamentoId: "",
      empresaId: ""
    });
  };

  if (loading) {
    return (
      <DashboardBase title="Gestión de Empresas" activeNav="empresas">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #1e40af',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <span style={{ marginLeft: '16px', color: '#64748b' }}>
            Cargando empresas...
          </span>
        </div>
      </DashboardBase>
    );
  }

  // Si no está autenticado, mostrar formulario de login
  if (!isAuthenticated) {
    return (
      <DashboardBase>
        <div style={{ padding: '40px', maxWidth: '400px', margin: '0 auto' }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '40px', 
            borderRadius: '12px', 
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: '30px', color: '#333' }}>🔐 Acceso Administrativo</h2>
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: '20px', textAlign: 'left' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Email de administrador:
                </label>
                <input
                  type="email"
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                  required
                />
              </div>
              <div style={{ marginBottom: '30px', textAlign: 'left' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Contraseña:
                </label>
                <input
                  type="password"
                  value={authPassword}
                  onChange={(e) => setAuthPassword(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                  required
                />
              </div>
              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                🚪 Iniciar Sesión
              </button>
            </form>
            <div style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
              <p>Email por defecto: admin@ossinnovation.com</p>
            </div>
          </div>
        </div>
      </DashboardBase>
    );
  }

  return (
    <DashboardBase title="Gestión de Empresas" activeNav="empresas">
      <div className="content-area">
        {/* Header con info de usuario autenticado */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <div>
            <h2 style={{ fontSize: '1.5em', fontWeight: '600', marginBottom: '4px' }}>
              Empresas Registradas
            </h2>
            <p style={{ margin: 0, color: '#666', fontSize: '0.9em' }}>
              Conectado como: <strong>{currentUser?.email || authEmail}</strong>
            </p>
          </div>
          <button
            onClick={handleLogout}
            style={{
              padding: '8px 16px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🚪 Cerrar Sesión
          </button>
        </div>
        
        {/* Botones de acción */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <div>
            <p style={{ color: '#64748b', margin: 0 }}>
              Gestiona las empresas que utilizan el sistema de evaluación 360°
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary"
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '600'
            }}
          >
            ➕ Nueva Empresa
          </button>
        </div>

        {/* Formulario de Empresa */}
        {showForm && (
          <div className="card" style={{ marginBottom: '24px' }}>
            <div className="card-header">
              <h3 className="card-title">
                {editingEmpresa ? 'Editar Empresa' : 'Nueva Empresa'}
              </h3>
            </div>
            <form onSubmit={handleSave}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                gap: '20px',
                marginBottom: '24px'
              }}>
                <div className="form-group">
                  <label className="form-label">Nombre de la Empresa</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    required
                    placeholder="Ingresa el nombre de la empresa"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">NIT/Cédula</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.nitCedula}
                    onChange={(e) => setFormData({...formData, nitCedula: e.target.value})}
                    required
                    placeholder="123456789-0"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Sede</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.sede}
                    onChange={(e) => setFormData({...formData, sede: e.target.value})}
                    required
                    placeholder="Sede principal, sucursal, etc."
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">País</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.pais}
                    onChange={(e) => setFormData({...formData, pais: e.target.value})}
                    required
                    placeholder="Colombia, México, etc."
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Dirección</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.direccion}
                    onChange={(e) => setFormData({...formData, direccion: e.target.value})}
                    placeholder="Dirección completa"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    placeholder="01-234-5678"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="contacto@empresa.com"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Contacto Principal</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.contacto}
                    onChange={(e) => setFormData({...formData, contacto: e.target.value})}
                    placeholder="Nombre del contacto"
                  />
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="submit" className="btn btn-primary">
                  {editingEmpresa ? 'Actualizar' : 'Guardar'} Empresa
                </button>
                <button 
                  type="button" 
                  onClick={handleCancel}
                  className="btn btn-outline"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Formulario de Departamentos */}
        {showDepartamentosForm && selectedEmpresa && (
          <div className="card" style={{ marginBottom: '24px' }}>
            <div className="card-header">
              <h3 className="card-title">
                Crear Departamentos - {selectedEmpresa.nombre}
              </h3>
            </div>
            <form onSubmit={handleSaveDepartamento}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                gap: '20px',
                marginBottom: '24px'
              }}>
                <div className="form-group">
                  <label className="form-label">Nombre del Departamento/Área</label>
                  <input
                    type="text"
                    className="form-input"
                    value={departamentoData.nombre}
                    onChange={(e) => setDepartamentoData({...departamentoData, nombre: e.target.value})}
                    required
                    placeholder="Recursos Humanos, Ventas, etc."
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Nivel Jerárquico</label>
                  <select
                    className="form-input"
                    value={departamentoData.nivel}
                    onChange={(e) => setDepartamentoData({...departamentoData, nivel: e.target.value})}
                    required
                  >
                    {nivelesJerarquicos.map(nivel => (
                      <option key={nivel.value} value={nivel.value}>
                        {nivel.label} - {nivel.description}
                      </option>
                    ))}
                  </select>
                </div>
                
                {departamentos.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Departamento Padre (Opcional)</label>
                    <select
                      className="form-input"
                      value={departamentoData.parentId}
                      onChange={(e) => setDepartamentoData({...departamentoData, parentId: e.target.value})}
                    >
                      <option value="">Sin departamento padre</option>
                      {departamentos.map(dept => (
                        <option key={dept.id} value={dept.id}>
                          {dept.nombre} ({nivelesJerarquicos.find(n => n.value === dept.nivel)?.label})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="submit" className="btn btn-primary">
                  Guardar Departamento
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowCargosForm(true)}
                  className="btn btn-outline"
                  disabled={departamentos.length === 0}
                >
                  Ir a Crear Cargos
                </button>
                <button 
                  type="button" 
                  onClick={handleCancel}
                  className="btn btn-outline"
                >
                  Finalizar
                </button>
              </div>
            </form>
            
            {/* Lista de departamentos creados */}
            {departamentos.length > 0 && (
              <div style={{ marginTop: '24px' }}>
                <h4>Departamentos Creados:</h4>
                <div style={{ display: 'grid', gap: '8px', marginTop: '12px' }}>
                  {departamentos.map(dept => (
                    <div key={dept.id} style={{
                      padding: '12px',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <strong>{dept.nombre}</strong> - {nivelesJerarquicos.find(n => n.value === dept.nivel)?.label}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Formulario de Cargos */}
        {showCargosForm && selectedEmpresa && (
          <div className="card" style={{ marginBottom: '24px' }}>
            <div className="card-header">
              <h3 className="card-title">
                Crear Cargos - {selectedEmpresa.nombre}
              </h3>
            </div>
            <form onSubmit={handleSaveCargo}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                gap: '20px',
                marginBottom: '24px'
              }}>
                <div className="form-group">
                  <label className="form-label">Departamento</label>
                  <select
                    className="form-input"
                    value={cargoData.departamentoId}
                    onChange={(e) => setCargoData({...cargoData, departamentoId: e.target.value})}
                    required
                  >
                    <option value="">Seleccione un departamento</option>
                    {departamentos.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.nombre} ({nivelesJerarquicos.find(n => n.value === dept.nivel)?.label})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Nombre del Cargo</label>
                  <input
                    type="text"
                    className="form-input"
                    value={cargoData.nombre}
                    onChange={(e) => setCargoData({...cargoData, nombre: e.target.value})}
                    required
                    placeholder="Gerente, Analista, Coordinador, etc."
                  />
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="submit" className="btn btn-primary">
                  Guardar Cargo
                </button>
                <button 
                  type="button" 
                  onClick={handleCancel}
                  className="btn btn-outline"
                >
                  Finalizar
                </button>
              </div>
            </form>
            
            {/* Lista de cargos creados */}
            {cargos.length > 0 && (
              <div style={{ marginTop: '24px' }}>
                <h4>Cargos Creados:</h4>
                <div style={{ display: 'grid', gap: '8px', marginTop: '12px' }}>
                  {cargos.map(cargo => {
                    const dept = departamentos.find(d => d.id === cargo.departamentoId);
                    return (
                      <div key={cargo.id} style={{
                        padding: '12px',
                        background: '#f0f9ff',
                        borderRadius: '8px',
                        border: '1px solid #0ea5e9'
                      }}>
                        <strong>{cargo.nombre}</strong> - {dept?.nombre}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lista de empresas */}
        {!showForm && !showDepartamentosForm && !showCargosForm && (
          empresas.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
              <div style={{ fontSize: '3em', marginBottom: '16px' }}>🏢</div>
              <h3 style={{ marginBottom: '8px' }}>No hay empresas registradas</h3>
              <p style={{ color: '#64748b', marginBottom: '24px' }}>
                Comienza agregando la primera empresa al sistema
              </p>
              <button
                onClick={() => setShowForm(true)}
                className="btn btn-primary"
              >
                Agregar Primera Empresa
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {empresas.map((empresa) => (
                <div key={empresa.id} className="card">
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '16px'
                  }}>
                    <div>
                      <h3 style={{ fontSize: '1.2em', fontWeight: '600', marginBottom: '4px' }}>
                        {empresa.nombre}
                      </h3>
                      <p style={{ color: '#64748b', fontSize: '0.9em' }}>
                        NIT/Cédula: {empresa.nitCedula || empresa.ruc}
                      </p>
                      {empresa.sede && (
                        <p style={{ color: '#64748b', fontSize: '0.9em' }}>
                          Sede: {empresa.sede}
                        </p>
                      )}
                      {empresa.pais && (
                        <p style={{ color: '#64748b', fontSize: '0.9em' }}>
                          País: {empresa.pais}
                        </p>
                      )}
                    </div>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '0.8em',
                      fontWeight: '500',
                      backgroundColor: empresa.estado === 'activo' ? '#dcfce7' : '#fee2e2',
                      color: empresa.estado === 'activo' ? '#166534' : '#991b1b'
                    }}>
                      {empresa.estado}
                    </span>
                  </div>

                  {empresa.direccion && (
                    <p style={{ color: '#64748b', fontSize: '0.9em', marginBottom: '8px' }}>
                      📍 {empresa.direccion}
                    </p>
                  )}

                  {empresa.telefono && (
                    <p style={{ color: '#64748b', fontSize: '0.9em', marginBottom: '8px' }}>
                      📞 {empresa.telefono}
                    </p>
                  )}

                  {empresa.email && (
                    <p style={{ color: '#64748b', fontSize: '0.9em', marginBottom: '8px' }}>
                      ✉️ {empresa.email}
                    </p>
                  )}

                  {empresa.contacto && (
                    <p style={{ color: '#64748b', fontSize: '0.9em', marginBottom: '16px' }}>
                      👤 {empresa.contacto}
                    </p>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleEdit(empresa)}
                        className="btn btn-outline"
                        style={{ flex: 1, fontSize: '0.9em' }}
                      >
                        ✏️ Editar
                      </button>
                      <button
                        onClick={() => handleDelete(empresa.id)}
                        className="btn btn-danger"
                        style={{ flex: 1, fontSize: '0.9em' }}
                      >
                        🗑️ Eliminar
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleManageDepartamentos(empresa)}
                        className="btn btn-primary"
                        style={{ flex: 1, fontSize: '0.9em' }}
                      >
                        🏢 Departamentos
                      </button>
                      <button
                        onClick={() => handleManageCargos(empresa)}
                        className="btn btn-primary"
                        style={{ flex: 1, fontSize: '0.9em' }}
                      >
                        💼 Cargos
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </DashboardBase>
  );
};

export default EmpresasManager;
